import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import CategoryCard from './components/CategoryCard';
import ArticleCard from './components/ArticleCard';
import FilterPanel from './components/FilterPanel';
import ViewArticleModal from '../../components/modals/ViewArticleModal';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Input } from '../../components/ui';
import { articleService } from '../../api/articleService';
import {
  buildKbCategoryCards,
  buildKbTagFilters,
  extractArticleDetail,
  isPublishedKbArticle,
  mapKbArticleForPortal,
  parsePublishedKbList,
  resolveKbTenantId,
  toViewArticleModalShape,
} from '../../utils/knowledgeArticleDisplay';
import {
  Search, BookOpen, Eye, Clock,
  ChevronRight, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown,
  User, Tag, ArrowRight, AlertCircle,
  HelpCircle, Settings, Shield, Home, ExternalLink, X
} from 'lucide-react';

const UnifiedKnowledgeBase = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'browse';

  const [searchHistory, setSearchHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('kb_search_history');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [articles, setArticles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState(null);
  const [viewArticle, setViewArticle] = useState(null);
  const [viewArticleOpen, setViewArticleOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [faqSearchQuery, setFaqSearchQuery] = useState('');
  const [selectedFaqCategory, setSelectedFaqCategory] = useState('all');
  const [expandedFaqItems, setExpandedFaqItems] = useState(new Set());
  const searchSeqRef = useRef(0);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await articleService.getArticles({
          includeDrafts: false,
          tenantId: resolveKbTenantId(),
        });
        setArticles(parsePublishedKbList(response));
      } catch (err) {
        setError('Failed to fetch knowledge base data.');
        console.error('Error fetching knowledge base data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  const categories = useMemo(() => buildKbCategoryCards(articles), [articles]);
  const tagFilters = useMemo(() => buildKbTagFilters(articles), [articles]);

  const faqs = useMemo(
    () =>
      articles
        .filter((a) => a.question && a.content)
        .map((a) => ({
          id: a.id,
          question: a.question,
          answer: a.content,
          category: (a.category || 'general').toLowerCase(),
          tags: a.tags,
          views: a.views,
          votes: { helpful: a.helpfulCount || 0, notHelpful: a.notHelpfulCount || 0 },
          updatedAt: a.updatedAt,
          relatedArticles: [],
        })),
    [articles]
  );

  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const filterFAQs = useCallback(() => {
    let filtered = faqs;

    if (faqSearchQuery) {
      filtered = filtered.filter(faq => 
        faq.question.toLowerCase().includes(faqSearchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(faqSearchQuery.toLowerCase())
      );
    }

    if (selectedFaqCategory !== 'all') {
      filtered = filtered.filter(faq => faq.category === selectedFaqCategory);
    }

    setFilteredFaqs(filtered);
  }, [faqs, faqSearchQuery, selectedFaqCategory]);

  useEffect(() => {
    filterFAQs();
  }, [filterFAQs]);

  // Save search history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('kb_search_history', JSON.stringify(searchHistory));
    } catch (err) {
      console.error('Failed to save search history:', err);
    }
  }, [searchHistory]);

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, setSearchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleSearch = async (query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    const seq = ++searchSeqRef.current;

    setSearchQuery(trimmedQuery);
    setLastSearchQuery(trimmedQuery);
    setActiveTab('search');
    setSelectedCategory('all');
    setSelectedTag(null);
    setSearchLoading(true);
    setError(null);

    try {
      const response = await articleService.searchArticles(trimmedQuery, {
        tenantId: resolveKbTenantId(),
        includeDrafts: false,
      });
      if (seq !== searchSeqRef.current) return;
      setSearchResults(parsePublishedKbList(response));
      if (!searchHistory.includes(trimmedQuery)) {
        setSearchHistory((prev) => [trimmedQuery, ...prev].slice(0, 10));
      }
    } catch (err) {
      console.error('KB search failed:', err);
      if (seq !== searchSeqRef.current) return;
      setSearchResults([]);
      setError('Search failed. Please try again.');
    } finally {
      if (seq === searchSeqRef.current) {
        setSearchLoading(false);
      }
    }
  };

  const handleOpenArticle = async (article) => {
    const id = article?.articleId ?? article?.id;
    if (!id) return;

    setDetailLoading(true);
    try {
      const response = await articleService.getArticleById(id);
      const raw = extractArticleDetail(response);
      if (!raw || !isPublishedKbArticle(raw)) {
        setError('Article is not available.');
        return;
      }
      const mapped = mapKbArticleForPortal(raw);
      setViewArticle(toViewArticleModalShape(mapped));
      setViewArticleOpen(true);
      articleService.incrementUsedCount(id).catch(() => {});
    } catch (err) {
      console.error('Failed to load article:', err);
      setError('Failed to load article.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCategorySelect = (categoryTitle) => {
    // Normalize category name for filtering
    const normalizedCategory = categoryTitle.toLowerCase().replace(/\s+/g, '-');
    
    // Toggle: if already selected, clear it
    if (selectedCategory === normalizedCategory || selectedCategory === categoryTitle.toLowerCase()) {
      setSelectedCategory('all');
    } else {
      setSelectedCategory(normalizedCategory);
      // Clear selected tag when selecting category
      setSelectedTag(null);
    }
    
    // Clear search query when selecting category
    setSearchQuery('');
    setLastSearchQuery('');
    setSearchResults([]);
    // Stay on browse tab and show filtered articles
    setActiveTab('browse');
    
    // Scroll to articles section if needed
    setTimeout(() => {
      const articlesSection = document.querySelector('[data-articles-section]');
      if (articlesSection) {
        articlesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleTagSelect = (tagName) => {
    // Toggle: if already selected, clear it
    if (selectedTag === tagName || selectedTag?.toLowerCase() === tagName.toLowerCase()) {
      setSelectedTag(null);
    } else {
      // Set selected tag for filtering
      setSelectedTag(tagName);
      // Clear category when selecting tag
      setSelectedCategory('all');
    }
    
    // Clear search when selecting tag
    setSearchQuery('');
    setLastSearchQuery('');
    setSearchResults([]);
    // Stay on browse tab and show filtered articles
    setActiveTab('browse');
    
    // Scroll to articles section if needed
    setTimeout(() => {
      const articlesSection = document.querySelector('[data-articles-section]');
      if (articlesSection) {
        articlesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const toggleFaqExpanded = (faqId) => {
    const newExpanded = new Set(expandedFaqItems);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedFaqItems(newExpanded);
  };

  const handleFaqVote = () => {
    // FAQ helpful votes are display-only in portal B2 (live data has counts from API).
  };

  const filteredBrowseArticles = useMemo(() => {
    let list = [...articles];
    if (selectedCategory !== 'all') {
      list = list.filter((a) => a.categoryKey === selectedCategory);
    }
    if (selectedTag) {
      const tagLower = selectedTag.toLowerCase();
      list = list.filter((a) =>
        a.tags?.some(
          (tag) => tag.toLowerCase() === tagLower || tag.toLowerCase().includes(tagLower)
        )
      );
    }
    return list.sort((a, b) => (b.views || 0) - (a.views || 0));
  }, [articles, selectedCategory, selectedTag]);

  const recentArticles = useMemo(
    () =>
      [...filteredBrowseArticles]
        .sort((a, b) => new Date(b.lastUpdated || 0) - new Date(a.lastUpdated || 0))
        .slice(0, 5),
    [filteredBrowseArticles]
  );

  const filterPanelCategories = useMemo(
    () => categories.map((c) => ({ name: c.title, count: c.articleCount })),
    [categories]
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatViews = (views) => {
    if (!views) return '0';
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}k`;
    }
    return views.toString();
  };

  const getFaqCategories = () => {
    const categories = ['all', ...new Set(faqs.map(faq => faq.category))];
    return categories;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'general': HelpCircle,
      'account': User,
      'technical': Settings,
      'billing': Shield,
      'security': Shield
    };
    return icons[category] || HelpCircle;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'general': 'bg-gray-100 text-gray-800',
      'account': 'bg-blue-100 text-blue-800',
      'technical': 'bg-green-100 text-green-800',
      'billing': 'bg-purple-100 text-purple-800',
      'security': 'bg-red-100 text-red-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading knowledge base...</p>
        </div>
      </div>
    );
  }

  if (error && articles.length === 0 && !searchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'browse', label: 'Browse', icon: BookOpen },
    { id: 'search', label: 'Search', icon: Search },
    { id: 'faqs', label: 'FAQs', icon: HelpCircle }
  ];

  const filteredArticles = filteredBrowseArticles;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header is provided by PortalHomeLayout */}
      
      <main className="flex-1">
        {/* Hero Section (from PortalKnowledgeBase) */}
        <section className="relative py-12 md:py-20 overflow-hidden">
          {/* Background Image */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'url(/background-image.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(2px)',
              opacity: 0.3
            }}
          ></div>
          {/* Color overlay for better text visibility */}
          <div className="absolute inset-0 bg-white/60"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Home className="h-4 w-4" />
              <ChevronRight className="h-4 w-4" />
              <span className="text-foreground font-medium">Knowledge Base</span>
            </div>

            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Knowledge Base
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-6 md:mb-8">
                Find answers, learn best practices, and explore our comprehensive documentation
              </p>
              
              {/* Search Bar */}
              <form 
                onSubmit={(e) => { 
                  e.preventDefault(); 
                  if (searchQuery.trim()) {
                    handleSearch(searchQuery);
                  }
                }} 
                className="relative"
              >
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  placeholder="Search for articles, guides, and FAQs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim()) {
                      e.preventDefault();
                      handleSearch(searchQuery);
                    }
                  }}
                  className="pl-12 h-12 md:h-14 text-base md:text-lg bg-card shadow-elegant border-2 focus-visible:ring-primary"
                />
                <Button 
                  type="submit"
                  size="lg"
                  className="absolute right-2 top-1/2 -translate-y-1/2 gradient-primary text-primary-foreground hover:opacity-90"
                  disabled={!searchQuery.trim()}
                >
                  Search
                </Button>
              </form>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-8 md:py-16 relative bg-gray-50">
          {/* Background Image */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'url(/background-image.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(2px)',
              opacity: 0.2
            }}
          ></div>
          {/* Color overlay for better text visibility */}
          <div className="absolute inset-0 bg-gray-50/70"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            {error && articles.length > 0 && (
              <div
                className="mb-6 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                role="alert"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1">{error}</span>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700"
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
              {/* Main Content Area */}
              <div className="lg:col-span-3 space-y-8 md:space-y-12">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200">
                  <nav className="flex space-x-4 md:space-x-8 overflow-x-auto">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => handleTabChange(tab.id)}
                          className={`flex items-center space-x-2 py-3 md:py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                            activeTab === tab.id
                              ? 'border-primary-500 text-primary-600'
                              : 'border-transparent text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <Icon size={18} />
                          <span>{tab.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>

                {/* Tab Content */}
                {/* Browse Tab (from PortalKnowledgeBase + KnowledgeBaseAccess) */}
                {activeTab === 'browse' && (
                  <div className="space-y-8 md:space-y-12">
                    {/* Categories Grid */}
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-foreground">Browse by Category</h2>
                      {categories.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                          {categories.map((category) => (
                            <CategoryCard
                              key={category.title}
                              {...category}
                              onClick={() => handleCategorySelect(category.title)}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">No categories available yet.</p>
                        </div>
                      )}
                    </div>

                    {/* Popular Articles - Show filtered articles when category or tag is selected */}
                    <div data-articles-section>
                      <div className="flex items-center justify-between mb-4 md:mb-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                          {selectedTag 
                            ? `Articles tagged "${selectedTag}"`
                            : selectedCategory !== 'all' 
                            ? `Articles in "${categories.find(c => c.title.toLowerCase().replace(/\s+/g, '-') === selectedCategory)?.title || selectedCategory}"`
                            : 'Popular Articles'}
                        </h2>
                        {(selectedCategory !== 'all' || selectedTag) && (
                          <Button 
                            variant="outline" 
                            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                            onClick={() => {
                              setSelectedCategory('all');
                              setSelectedTag(null);
                              setSearchQuery('');
                              setLastSearchQuery('');
                              setSearchResults([]);
                            }}
                          >
                            Clear Filter
                            <X className="h-4 w-4 ml-1" />
                          </Button>
                        )}
                        {selectedCategory === 'all' && !selectedTag && (
                          <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                            View All
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        )}
                      </div>
                      {filteredArticles.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                          {filteredArticles.slice(0, 6).map((article, index) => (
                            <div
                              key={article.id || index}
                              onClick={() => handleOpenArticle(article)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => e.key === 'Enter' && handleOpenArticle(article)}
                            >
                              <ArticleCard {...article} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600">
                            {selectedTag 
                              ? `No articles found with tag "${selectedTag}".`
                              : selectedCategory !== 'all'
                              ? `No articles found in this category.`
                              : 'No articles found.'}
                          </p>
                          <Button 
                            variant="outline" 
                            className="mt-4"
                            onClick={() => {
                              setSelectedCategory('all');
                              setSelectedTag(null);
                              setSearchQuery('');
                              setLastSearchQuery('');
                              setSearchResults([]);
                            }}
                          >
                            {selectedTag ? 'Clear Tag Filter' : 'View All Categories'}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Recent Updates */}
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6 text-foreground">Recent Updates</h2>
                      <div className="space-y-4">
                        {recentArticles.slice(0, 3).map((article, index) => (
                          <div
                            key={article.id || index}
                            className="p-4 md:p-6 border rounded-lg hover:border-primary hover:shadow-lg transition-smooth bg-card cursor-pointer"
                            onClick={() => handleOpenArticle(article)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && handleOpenArticle(article)}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <h3 className="font-semibold text-base md:text-lg mb-2 hover:text-primary transition-smooth">
                                  {article.title}
                                </h3>
                                <p className="text-sm text-muted-foreground mb-3">
                                  {article.excerpt || article.summary}
                                </p>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                  <span>{article.category}</span>
                                  <span>•</span>
                                  <span>{article.readTime || '5 min read'}</span>
                                  <span>•</span>
                                  <span>{formatViews(article.views)} views</span>
                                </div>
                              </div>
                              <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Search Tab (from KnowledgeBaseAccess) */}
                {activeTab === 'search' && (
                  <div className="space-y-6">
                    {/* Search History */}
                    {searchHistory.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle>Recent Searches</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2">
                            {searchHistory.map((historyItem, idx) => (
                              <Button
                                key={idx}
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSearchQuery(historyItem);
                                  handleSearch(historyItem);
                                }}
                                className="text-xs"
                              >
                                <Clock className="h-3 w-3 mr-1" />
                                {historyItem}
                              </Button>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <Card>
                      <CardHeader>
                        <CardTitle>Search Results</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {searchLoading ? (
                          <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
                            <p className="text-gray-600">Searching articles...</p>
                          </div>
                        ) : searchResults.length > 0 ? (
                          <div className="space-y-4">
                            {searchResults.map((article, index) => (
                              <div
                                key={article.id || index}
                                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleOpenArticle(article)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && handleOpenArticle(article)}
                              >
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900 mb-1">
                                    {article.title}
                                  </h4>
                                  <p className="text-sm text-gray-600 mb-2">
                                    {article.summary || article.excerpt}
                                  </p>
                                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                                    <div className="flex items-center space-x-1">
                                      <Eye className="h-3 w-3" />
                                      <span>{formatViews(article.views)}</span>
                                    </div>
                                    <Badge variant="outline">{article.category}</Badge>
                                    {article.tags && article.tags.slice(0, 3).map((tag, idx) => (
                                      <Badge key={idx} variant="secondary" className="text-xs">
                                        <Tag className="h-2 w-2 mr-1" />
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2 ml-4 text-primary">
                                  <ArrowRight className="h-4 w-4" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">
                              {lastSearchQuery ? 'No articles found matching your search.' : 'Enter a search term to find articles.'}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* FAQs Tab (from FAQs) */}
                {activeTab === 'faqs' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
                      <Badge variant="secondary">
                        {filteredFaqs.length} questions
                      </Badge>
                    </div>

                    {/* Search and Filter */}
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <Input
                                placeholder="Search FAQs..."
                                value={faqSearchQuery}
                                onChange={(e) => setFaqSearchQuery(e.target.value)}
                                className="pl-10"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <select
                              value={selectedFaqCategory}
                              onChange={(e) => setSelectedFaqCategory(e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {getFaqCategories().map(category => (
                                <option key={category} value={category}>
                                  {category === 'all' ? 'All Categories' : category.charAt(0).toUpperCase() + category.slice(1)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* FAQs List */}
                    <div className="space-y-4">
                      {filteredFaqs.length === 0 ? (
                        <Card>
                          <CardContent className="text-center py-12">
                            <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                              No FAQs found
                            </h3>
                            <p className="text-gray-500">
                              {faqSearchQuery ? 'Try adjusting your search terms' : 'No FAQs available in this category'}
                            </p>
                          </CardContent>
                        </Card>
                      ) : (
                        filteredFaqs.map((faq) => {
                          const CategoryIcon = getCategoryIcon(faq.category);
                          return (
                            <Card key={faq.id} className="hover:shadow-md transition-shadow">
                              <CardContent className="p-0">
                                <div
                                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                                  onClick={() => toggleFaqExpanded(faq.id)}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <CategoryIcon className="h-4 w-4" />
                                        <Badge 
                                          variant="outline" 
                                          className={`text-xs ${getCategoryColor(faq.category)}`}
                                        >
                                          {faq.category}
                                        </Badge>
                                      </div>
                                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        {faq.question}
                                      </h3>
                                      <div className="flex items-center gap-4 text-sm text-gray-500">
                                        <span>{faq.views} views</span>
                                        <span>{faq.votes.helpful + faq.votes.notHelpful} votes</span>
                                        <span>Updated {formatDate(faq.updatedAt)}</span>
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      {expandedFaqItems.has(faq.id) ? (
                                        <ChevronUp className="h-5 w-5 text-gray-400" />
                                      ) : (
                                        <ChevronDown className="h-5 w-5 text-gray-400" />
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {expandedFaqItems.has(faq.id) && (
                                  <div className="px-6 pb-6 border-t border-gray-200">
                                    <div className="pt-4">
                                      <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                                        {faq.answer}
                                      </div>
                                      
                                      {faq.relatedArticles && faq.relatedArticles.length > 0 && (
                                        <div className="mt-4">
                                          <h4 className="text-sm font-medium text-gray-900 mb-2">
                                            Related Articles:
                                          </h4>
                                          <div className="flex flex-wrap gap-2">
                                            {faq.relatedArticles.map((article, idx) => (
                                              <Button
                                                key={idx}
                                                variant="outline"
                                                size="sm"
                                                className="text-xs"
                                              >
                                                <ExternalLink className="h-3 w-3 mr-1" />
                                                {article.title}
                                              </Button>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      <div className="mt-4 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm text-gray-500">Was this helpful?</span>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => { e.stopPropagation(); handleFaqVote(faq.id, 'helpful'); }}
                                            className="text-green-600 hover:text-green-700"
                                          >
                                            <ThumbsUp className="h-4 w-4 mr-1" />
                                            {faq.votes.helpful}
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => { e.stopPropagation(); handleFaqVote(faq.id, 'notHelpful'); }}
                                            className="text-red-600 hover:text-red-700"
                                          >
                                            <ThumbsDown className="h-4 w-4 mr-1" />
                                            {faq.votes.notHelpful}
                                          </Button>
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                          {faq.tags.map((tag, idx) => (
                                            <Badge key={idx} variant="secondary" className="text-xs">
                                              {tag}
                                            </Badge>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

              </div>

              {/* Sidebar (from PortalKnowledgeBase) - Fixed */}
              <div className="lg:col-span-1">
                <div className="sticky top-4" style={{ maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto' }}>
                  <FilterPanel
                    categories={filterPanelCategories}
                    tags={tagFilters}
                    onCategorySelect={handleCategorySelect}
                    onTagSelect={handleTagSelect}
                    selectedCategory={selectedCategory}
                    selectedTag={selectedTag}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <ViewArticleModal
        isOpen={viewArticleOpen}
        onClose={() => {
          setViewArticleOpen(false);
          setViewArticle(null);
        }}
        article={viewArticle}
      />

      {detailLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary bg-white p-4 rounded-full" />
        </div>
      )}

      {/* Footer is provided by PortalHomeLayout */}
    </div>
  );
};

export default UnifiedKnowledgeBase;

